/**
 * Rhythmz — Scene: Bioluminescent Forest
 * ──────────────────────────────────
 * Alien forest of giant glowing mushrooms with floating spores.
 *
 * Audio: Bass → stalk sway + cap pulse. Beat → spore burst + cap flash.
 */

import * as THREE from 'three/webgpu';
import { Fn, vec4, float, mul, add } from 'three/tsl';
import { SceneLighting, type AudioRef } from './SceneLighting';
import type { SceneHandle } from './SceneManager';

type N = any;

export interface ForestUniforms {
  uTime: N; uBass: N; uMid: N; uBeat: N; uTreble: N;
  uCol0: N; uCol1: N; uCol2: N;
}

export function createBioluminescentForest(u: ForestUniforms): SceneHandle {
  const group = new THREE.Group();
  const lighting = new SceneLighting({
    keyColor: new THREE.Color(0.4, 0.3, 0.6),
    keyIntensity: 6, keyPosition: new THREE.Vector3(0, 6, 3),
    fillColor: new THREE.Color(0.8, 0.2, 0.5),
    fillIntensity: 4, fillPosition: new THREE.Vector3(0, -2, 0),
    rimColor: new THREE.Color(0.1, 0.5, 0.7),
    rimIntensity: 5, rimPosition: new THREE.Vector3(0, 0, -6),
    camLightIntensity: 2,
  });
  group.add(lighting.group);

  // ── Mushroom stalks + caps (instanced) ──
  const mushroomCount = 7;
  const stalkGeo = new THREE.CylinderGeometry(0.08, 0.15, 1.5, 8, 4);
  const stalkMat = new THREE.MeshStandardNodeMaterial({ metalness: 0.1, roughness: 0.6, color: 0x1a0a1a });
  stalkMat.emissiveNode = Fn(() => {
    const glow = add(float(0.05), mul(u.uBass, float(0.15)));
    return vec4(mul(float(0.3), glow), mul(float(0.1), glow), mul(float(0.4), glow), float(1.0));
  })();
  const stalks = new THREE.InstancedMesh(stalkGeo, stalkMat, mushroomCount);
  stalks.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  group.add(stalks);

  const capGeo = new THREE.SphereGeometry(0.4, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.5);
  const capMat = new THREE.MeshStandardNodeMaterial({ metalness: 0.0, roughness: 0.3 });
  capMat.emissiveNode = Fn(() => {
    const pulse = add(float(0.3), mul(u.uBeat, float(1.0)), mul(u.uBass, float(0.5)));
    return vec4(mul(float(0.9), pulse), mul(float(0.2), pulse), mul(float(0.6), pulse), float(1.0));
  })();
  const caps = new THREE.InstancedMesh(capGeo, capMat, mushroomCount);
  caps.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  group.add(caps);

  // Mushroom positions
  const mushData = Array.from({ length: mushroomCount }, (_, i) => {
    const angle = (i / mushroomCount) * Math.PI * 2;
    const r = 1 + Math.random() * 1.5;
    return { x: Math.cos(angle) * r, z: Math.sin(angle) * r, h: 0.8 + Math.random() * 0.8, phase: Math.random() * 6.28 };
  });

  // ── Spores: points ──
  const sporeCount = 300;
  const sporePositions = new Float32Array(sporeCount * 3);
  for (let i = 0; i < sporeCount; i++) {
    sporePositions[i * 3] = (Math.random() - 0.5) * 6;
    sporePositions[i * 3 + 1] = Math.random() * 3;
    sporePositions[i * 3 + 2] = (Math.random() - 0.5) * 6;
  }
  const sporeGeo = new THREE.BufferGeometry();
  sporeGeo.setAttribute('position', new THREE.BufferAttribute(sporePositions, 3));
  const sporeMat = new THREE.PointsMaterial({ color: 0xffaa44, size: 0.05, transparent: true, opacity: 0.8 });
  const spores = new THREE.Points(sporeGeo, sporeMat);
  group.add(spores);

  let t = 0;
  const pivot = new THREE.Object3D();

  const update = (dt: number): void => {
    t += dt;
    const audio: AudioRef = {
      bass: u.uBass.value ?? 0, beat: u.uBeat.value ?? 0, mid: u.uMid.value ?? 0,
      level: ((u.uBass.value ?? 0) + (u.uMid.value ?? 0)) * 0.5,
    };
    lighting.update(audio);

    // Stalks sway
    mushData.forEach((m, i) => {
      const sway = Math.sin(t * 0.8 + m.phase) * 0.1 * (1 + (u.uBass.value ?? 0));
      pivot.position.set(m.x, m.h * 0.5, m.z);
      pivot.rotation.set(sway * 0.5, 0, sway);
      pivot.scale.set(1, m.h, 1);
      pivot.updateMatrix();
      stalks.setMatrixAt(i, pivot.matrix);
      // Caps on top
      pivot.position.set(m.x + sway * m.h, m.h + 0.1, m.z);
      pivot.rotation.set(sway, 0, 0);
      const capPulse = 1 + (u.uBeat.value ?? 0) * 0.2;
      pivot.scale.setScalar(capPulse);
      pivot.updateMatrix();
      caps.setMatrixAt(i, pivot.matrix);
    });
    stalks.instanceMatrix.needsUpdate = true;
    caps.instanceMatrix.needsUpdate = true;

    // Spores rise
    const positions = sporeGeo.getAttribute('position') as THREE.BufferAttribute;
    for (let i = 0; i < sporeCount; i++) {
      let y = positions.getY(i) + dt * 0.3;
      if (y > 3) y = 0;
      positions.setY(i, y);
      positions.setX(i, positions.getX(i) + Math.sin(t + i) * dt * 0.1);
    }
    positions.needsUpdate = true;

    group.rotation.y = t * 0.05;
  };

  const dispose = (): void => {
    lighting.dispose();
    stalkGeo.dispose(); stalkMat.dispose();
    capGeo.dispose(); capMat.dispose();
    sporeGeo.dispose(); sporeMat.dispose();
    group.clear();
  };

  return { group, update, dispose, name: 'Bioluminescent Forest' };
}